import { action, computed, makeAutoObservable, observable } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import { createContext, useContext, useEffect, useRef, useState } from "react";

import { LLMProviderContext } from "./provider-store";

import { Model, useModels } from "@/queries/models";

export class ModelsStore {
  @observable _models: Model[] = [];
  @observable _modelsLoading = false;
  @observable _modelsPage = 1;
  @observable _modelsPageSize = 250;
  @observable _selectedProviderId: number | undefined = undefined;
  @observable _isEnabled = false;
  @observable _search = "";
  @observable _allowEmptySearch = false;
  @observable _fromProviderId?: number;
  @observable _modelParameterOverrides?: Record<string, unknown>;
  // Server-side api_type scope avoids preloading large catalogs (e.g. OpenRouter).
  @observable _selectedApiType?: string | null;

  constructor(providerId?: number) {
    makeAutoObservable(this);
    this._selectedProviderId = providerId;

    if (providerId) {
      this._isEnabled = true;
    }
  }

  @action setModels = (models: Model[]) => {
    this._models = models;
  };

  @action setModelsLoading = (modelsLoading: boolean) => {
    this._modelsLoading = modelsLoading;
  };

  @action setModelsPageSize = (modelsPageSize: number) => {
    this._modelsPageSize = modelsPageSize;
  };

  @action setModelsPage = (modelsPage: number) => {
    this._modelsPage = modelsPage;
  };

  @action setSelectedProviderId = (providerId: number | undefined) => {
    if (this._selectedProviderId !== providerId) {
      this._fromProviderId = this._selectedProviderId;
      this._selectedProviderId = providerId;
      this._models = [];
      // Clear stale api_type scope when switching providers.
      this._selectedApiType = undefined;
    }
  };

  @action setSelectedApiType = (apiType: string | null | undefined) => {
    if (this._selectedApiType !== apiType) {
      this._selectedApiType = apiType;
    }
  };

  @computed get selectedApiType() {
    return this._selectedApiType;
  }

  @computed get fromProviderId() {
    if (!this._fromProviderId) {
      return this.selectedProviderId;
    }
    return this._fromProviderId;
  }

  @action setModelParameterOverrides = (overrides: Record<string, unknown>) => {
    this._modelParameterOverrides = overrides;
  };

  @computed get modelParameterOverrides() {
    return this._modelParameterOverrides;
  }

  @computed get models() {
    return this._models;
  }

  @computed get modelsLoading() {
    return this._modelsLoading;
  }

  @computed get modelsPage() {
    return this._modelsPage;
  }

  @computed get modelsPageSize() {
    return this._modelsPageSize;
  }

  @computed get selectedProviderId() {
    return this._selectedProviderId;
  }

  @computed get isEnabled() {
    return this._isEnabled;
  }

  @computed get search() {
    return this._search;
  }

  @computed get allowEmptySearch() {
    return this._allowEmptySearch;
  }

  @action setSearch = (search: string) => {
    this._search = search;
  };

  @action setAllowEmptySearch = (allowEmptySearch: boolean) => {
    this._allowEmptySearch = allowEmptySearch;
  };

  @action reset = () => {
    this._models = [];
    this._modelsLoading = false;
    this._modelsPage = 1;
    this._selectedProviderId = undefined;
  };
}

export interface ModelsContextType {
  store: ModelsStore;
  setModelsPage: React.Dispatch<React.SetStateAction<number>>;
  isEnabled: boolean;
  setIsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}

const ModelsContext = createContext<ModelsContextType | undefined>(undefined);

export const ModelsStoreProvider = observer(
  ({ children, providerId }: { children: React.ReactNode; providerId?: number }) => {
    const [isEnabled, setIsEnabled] = useState(false);
    const providerContext = useContext(LLMProviderContext);
    const { providers } = providerContext?.store || {};
    const store = useLocalObservable(() => new ModelsStore(providerId));
    const isCustomProvider = Boolean(
      providers?.find(provider => provider.id === store.selectedProviderId)?.workspace_id,
    );

    const {
      data: models,
      isLoading: modelsLoading,
      isRefetching: modelsRefetching,
      setPage: setModelsPage,
    } = useModels({
      providerId: store.search && isCustomProvider ? undefined : store.selectedProviderId,
      q: store.search,
      allowEmptySearch: store.allowEmptySearch,
      fromProviderId: store.fromProviderId,
      apiType: store.selectedApiType,
    });

    useEffect(() => {
      if (models?.model_configs && !modelsLoading && !modelsRefetching) {
        store.setModels(models.model_configs);
      }
    }, [models?.model_configs, modelsLoading, modelsRefetching, store]);

    useEffect(() => {
      store.setModelsLoading(modelsLoading || modelsRefetching);
    }, [modelsLoading, modelsRefetching, store]);

    return (
      <ModelsContext.Provider
        value={{
          store,
          setModelsPage,
          isEnabled,
          setIsEnabled,
        }}
      >
        {children}
      </ModelsContext.Provider>
    );
  },
);

export const useModelsStore = ({
  isEnabledByDefault = false,
  allowEmptySearch = false,
}: {
  isEnabledByDefault?: boolean;
  allowEmptySearch?: boolean;
}): ModelsContextType => {
  const context = useContext(ModelsContext);

  if (!context) {
    throw new Error("useModelsStore must be used within a ModelsStoreProvider");
  }

  const hasSetAllowEmptySearch = useRef(false);
  const hasSetIsEnabled = useRef(false);

  useEffect(() => {
    if (allowEmptySearch && !context.store.allowEmptySearch && !hasSetAllowEmptySearch.current) {
      hasSetAllowEmptySearch.current = true;
      context.store.setAllowEmptySearch(true);
    }
  }, [allowEmptySearch, context.store]);

  useEffect(() => {
    if (isEnabledByDefault && !context.isEnabled && !hasSetIsEnabled.current) {
      hasSetIsEnabled.current = true;
      context.setIsEnabled(true);
    }
  }, [isEnabledByDefault, context]);

  return context;
};
